import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/autoplay';

import { FC, useEffect, useMemo, useState } from 'react';
import { Autoplay, Pagination } from 'swiper';
import { Swiper as SwiperWrapper, SwiperSlide, useSwiper } from 'swiper/react';

import Banner from '@/components/elements/Banner';
import { Dummy } from '@/components/elements/Image';
import { useUserContext } from '@/contexts/user';
import useScript, { Status } from '@/hooks/useScript';
import { getSkyflagLink } from '@/lib/dmm/point';
import { BannerAreaLabel } from '@/lib/dmm/trackingTag/type';
import { getLink } from '@/lib/mainvisual';
import { Banner as BannerItem } from '@/lib/microcms/model';

import {
  BANNER_ASPECT_RATIO,
  Container,
  DummyItem,
  ListItem,
  NavigationButton,
} from './style';

interface MainVisualContentType {
  mainVisuals: BannerItem[];
}

const NavigationBtn: FC<{
  iconType: 'next' | 'prev';
  slideWidth: string;
}> = ({ iconType, slideWidth }) => {
  const swiper = useSwiper();

  const handleSubmit = () => {
    switch (iconType) {
      case 'next': {
        swiper.slideNext();
        break;
      }
      case 'prev': {
        swiper.slidePrev();
        break;
      }
    }
  };
  return (
    <NavigationButton
      iconType={iconType}
      slideWidth={slideWidth}
      onClick={() => handleSubmit()}
    />
  );
};

const DUMMY_BANNER_COUNT = 4;

const MainVisual: FC<MainVisualContentType> = ({ mainVisuals }) => {
  const [slideWidth, setSlideWidth] = useState<string>(
    `${window.outerWidth}px`,
  );
  const [bannerWidth, setBannerWidth] = useState<number>(0);
  const { user } = useUserContext();

  const gtaScript = useScript('/customGtaScript.js?20241120');

  // MEMO: Swiper.jsの複製ロジック上、初期表示が2番目のバナーになるため最後尾のバナーを先頭にもってくるようにしている
  const bannerItems = useMemo(() => {
    if (!mainVisuals.length) {
      return [];
    }
    const lastIndex = mainVisuals.length - 1;
    const lastBanner = mainVisuals[lastIndex];
    return [
      lastBanner,
      ...mainVisuals.slice(0, lastIndex).map(mv => {
        if (mv.news?.id == 'info_20220525') {
          mv.news = undefined;
          mv.linkPath = getSkyflagLink(user.userId);
        }
        return mv;
      }),
    ];
  }, [mainVisuals]);

  const isExternal = (item: BannerItem) =>
    !getLink(item).startsWith(import.meta.env.BASE_URL);

  useEffect(() => {
    if (gtaScript === Status.Ready) {
      mainVisuals.forEach(b => {
        if (b.alt && b.alt === 'DMMプレミアム新規登録30日間無料') {
          window.sendPremiumBannerViewEvent(BannerAreaLabel.MainVisual);
        }
      });
    }
  }, [gtaScript]);

  /** クリックイベント */
  const clickEvent = (alt?: string, id?: string) => {
    if (alt && id && gtaScript === Status.Ready) {
      if (alt === 'DMMプレミアム新規登録30日間無料') {
        // プレミアムバナーのイベント通知
        window.sendPremiumBannerClickEvent(BannerAreaLabel.MainVisual);
      } else {
        // 上記以外のイベントのクリック通知
        window.sendKeirinBannerClickEvent(BannerAreaLabel.MainVisual, id, alt);
      }
    }
  };

  const getSubBannerId = (item: BannerItem) => {
    if (item.campaign) {
      return item.campaign.id;
    }
    if (item.blog) {
      return item.blog.id;
    }
    if (item.news) {
      return item.news.id;
    }
    return item.id;
  };

  return (
    <Container
      count={bannerItems.length ? mainVisuals.length : DUMMY_BANNER_COUNT}
    >
      <SwiperWrapper
        slidesPerView={1.2}
        centeredSlides={true}
        spaceBetween={8}
        loop={true}
        loopedSlides={bannerItems.length + 1}
        pagination={{
          clickable: true,
        }}
        breakpoints={{
          375: {
            slidesPerView: 1.3,
            spaceBetween: 16,
          },
          1024: {
            slidesPerView: 'auto',
          },
        }}
        autoplay={{
          delay: 5000,
        }}
        modules={[Pagination, Autoplay]}
        onResize={swiper => {
          const style = window.getComputedStyle(swiper.slides[0]);
          setSlideWidth(style.width);
        }}
        onInit={swiper => {
          setBannerWidth(swiper.slides[0].clientWidth);
        }}
      >
        {bannerItems.length ? (
          <>
            {bannerItems
              .filter(item => (item.image ? true : false))
              .map(item => (
                <SwiperSlide key={item.id}>
                  {({ isActive }) => (
                    <ListItem
                      isActive={isActive}
                      onClick={() => {
                        clickEvent(item.alt, getSubBannerId(item));
                      }}
                    >
                      <Banner
                        srcSet={`${item.image.url}?w=${bannerWidth}&dpr=1 1x, ${item.image.url}?w=${bannerWidth}&dpr=2 2x`}
                        mediaSet={[
                          {
                            srcSet: `${item.image.url}?w=${bannerWidth}&dpr=1&fm=webp 1x, ${item.image.url}?w=${bannerWidth}&dpr=2&fm=webp 2x`,
                            type: 'image/webp',
                          },
                        ]}
                        aspectRatio={BANNER_ASPECT_RATIO}
                        alt={item.alt}
                        bannerId={getSubBannerId(item)}
                        link={getLink(item)}
                        external={isExternal(item)}
                        disabled={!getLink(item) || !isActive}
                        scrollContainer=".swiper-wrapper"
                      />
                    </ListItem>
                  )}
                </SwiperSlide>
              ))}
          </>
        ) : (
          <>
            {new Array(DUMMY_BANNER_COUNT).fill(0).map((_, i) => (
              <SwiperSlide key={`${i}-skelton`}>
                <DummyItem>
                  <Dummy />
                </DummyItem>
              </SwiperSlide>
            ))}
          </>
        )}
        <NavigationBtn iconType="next" slideWidth={slideWidth} />
        <NavigationBtn iconType="prev" slideWidth={slideWidth} />
      </SwiperWrapper>
    </Container>
  );
};

export default MainVisual;
